import Facade from '@/Facade'
import { Model } from '@/model/Model'

/** Used until WinningsConfig is loaded. Mirrors `MAX_WIN` there and the 2000x bet rules copy. */
const DEFAULT_MAX_WIN_BET_MULTIPLIER = 2000

/** Server flag: the round win was capped at the max win limit (misc_prizes.capamount_reached). */
export const isMaxWinCapReached = (model: Model): boolean => {
    return Boolean((model?.spinData as any)?.cap_amount_reached)
}

/** Max win the game may award, as a bet multiplier (`WinningsConfig.MAX_WIN`). */
export const getMaxWinBetMultiplier = (): number => {
    const configured = Number(Facade.winningsConfig?.MAX_WIN)

    return Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_MAX_WIN_BET_MULTIPLIER
}

/** Max win for the stake in play, in minor units. 0 when no stake is known yet (no cap to apply). */
export const getMaxWinAmount = (model: Model): number => {
    const bet = Number(model?.currentBet ?? 0)

    return Number.isFinite(bet) && bet > 0 ? getMaxWinBetMultiplier() * bet : 0
}

/**
 * Feature totals are read straight from the response, which keeps reporting what the server had
 * planned for the whole feature. Neither the Hold & Win / free spins end popup nor the Win Meter
 * may present more than the max win for the stake in play, so the clamp is unconditional: the
 * `cap_amount_reached` flag rides on the spin that hit the limit, not on every spin whose total
 * already sits above it.
 */
export const capFeatureWinAmount = (model: Model, amount: number): number => {
    if (!Number.isFinite(amount)) {
        return amount
    }

    const maxWin = getMaxWinAmount(model)

    return maxWin > 0 ? Math.min(amount, maxWin) : amount
}

/** Part of `amount` that still fits under the max win once `alreadyPresented` is counted. */
export const capWinIncrement = (model: Model, alreadyPresented: number, amount: number): number => {
    if (!Number.isFinite(amount)) {
        return amount
    }

    const maxWin = getMaxWinAmount(model)
    if (!(maxWin > 0)) {
        return amount
    }

    return Math.max(0, Math.min(amount, maxWin - Number(alreadyPresented ?? 0)))
}

/**
 * Reaching the cap ends the round on the spin that hit it: the player keeps the capped amount
 * and no further respin / free spin may be played.
 *
 * The response still describes the round the server had planned (remaining respins, free spins
 * left, a freshly awarded bonus), so those counters are neutralised here, at the single point
 * where a spin response is committed. Every downstream consumer — FinalState routing, the
 * Hold&Win board collect/exit, the free spins summary — already keys off them, so the normal
 * end-of-feature flow runs on this spin and the capped total is what gets presented.
 */
export const applyMaxWinCapToSpinData = (model: Model): boolean => {
    const spinData: any = model?.spinData

    if (!spinData || !isMaxWinCapReached(model)) {
        return false
    }

    const nextRoundType = String(spinData.next_round_type || '').toLowerCase()

    spinData.respins_left = 0

    if (nextRoundType === 'respin' || nextRoundType === 'respins' || nextRoundType === 'freespins') {
        spinData.next_round_type = ''
    }

    if (spinData.freebet) {
        spinData.freebet.amount_left = 0
    }

    // A feature awarded on the capped spin can no longer pay anything out.
    if (spinData.bonus) {
        spinData.bonus.freebet_given = 0
    }

    return true
}
